<!--
  canonical product editor RoutingStrip — Day 4 (audit gap #5).

  Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
        02-product-editor.md (Day 4 — RoutingStrip).

  Sits between Stage and ColorFilmstrip. Renders the color > variant >
  artwork chain so the distributor can SEE which artwork a swatch click
  routed to, plus a readability verdict.

  Behavior:
    - No color selected -> placeholder row
    - Color selected + no placement -> color chip only
    - Color + placement -> full chain (color -> variant -> artwork) +
      readability badge

  The readability badge is a REAL WCAG ink-vs-garment contrast check
  (#2791), computed in utils/artworkReadability and shared with
  VariantCard. It renders only when the routed variant declares an ink
  color; with no declared ink there is no badge, rather than a
  hardcoded "Readable". The classifyLDWithName helper gives us
  light/dark on the garment for the chip label.
-->
<template>
    <div data-testid="routing-strip-root" class="routing-strip-root">
        <template v-if="!selectedColor">
            <div
                data-testid="routing-strip-placeholder"
                class="routing-strip-placeholder"
            >
                Click a swatch below to see the artwork it routes to.
            </div>
        </template>
        <template v-else>
            <span
                data-testid="routing-strip-color"
                class="routing-strip-chip"
            >
                <span
                    class="routing-strip-color-dot"
                    :style="colorDotStyle"
                    aria-hidden="true"
                ></span>
                <span class="routing-strip-chip-text">{{ selectedColorLabel }}</span>
            </span>
            <template v-if="hasFullChain">
                <span
                    data-testid="routing-strip-arrow-1"
                    class="routing-strip-arrow"
                    aria-hidden="true"
                >&rarr;</span>
                <span
                    data-testid="routing-strip-variant"
                    class="routing-strip-chip routing-strip-chip-variant"
                    :data-variant-kind="routedVariantKind || ''"
                >
                    <span class="routing-strip-chip-text">
                        {{ variantLabel }}
                    </span>
                </span>
                <span
                    data-testid="routing-strip-arrow-2"
                    class="routing-strip-arrow"
                    aria-hidden="true"
                >&rarr;</span>
                <span
                    data-testid="routing-strip-artwork"
                    class="routing-strip-chip routing-strip-chip-artwork"
                >
                    <span class="material-icons routing-strip-mi" aria-hidden="true">
                        {{ routedArtworkName ? "image" : "add_photo_alternate" }}
                    </span>
                    <span class="routing-strip-chip-text">{{
                        routedArtworkName || "No artwork"
                    }}</span>
                </span>
                <!--
                  #2791: rendered ONLY when a real verdict exists. A null
                  verdict means the ink color is unknown -- we show no
                  badge at all rather than an unearned "Readable".
                -->
                <span
                    v-if="readability"
                    data-testid="routing-strip-readability"
                    class="routing-strip-badge"
                    :data-state="readability.ok ? 'ok' : 'warn'"
                >
                    <span class="material-icons routing-strip-mi" aria-hidden="true">
                        {{ readability.ok ? "check_circle" : "warning" }}
                    </span>
                    <span class="routing-strip-chip-text">
                        {{ readability.label }} &middot; {{ readability.ratio.toFixed(1) }}:1
                    </span>
                </span>
            </template>
        </template>
    </div>
</template>

<script setup>
import { computed } from "vue";
import { useProductEditorStore } from "@/stores/productEditor";
import { useProductEditorUiState } from "@/stores/productEditorUiState";
import { useVariantResolver } from "@/composables/useVariantResolver";
import { variantKindLabel } from "@/utils/variantKindLabels";
import { formatArtworkName } from "@/utils/formatArtworkName";
import { readabilityFor } from "@/utils/artworkReadability";

const editor = useProductEditorStore();
const editorUi = useProductEditorUiState();
const { resolve } = useVariantResolver();

const selectedColor = computed(() => {
    if (!editorUi.selectedColorSku) return null;
    const colors = editor.supplierMeta?.colors ?? [];
    return (
        colors.find((c) => c.color_sku === editorUi.selectedColorSku) ?? null
    );
});

const colorDotStyle = computed(() => {
    const hex = selectedColor.value?.hex;
    if (hex) return { background: hex };
    return { background: "var(--tf-bg-muted, #f3f4f6)" };
});

// Day 4 smoke fix: against real seed (8084 CP 9), supplierMeta.colors
// may not resolve every selected_color_sku -- the same gap Day 3
// ColorFilmstrip handles with synthesized swatches. Fall back to the
// color_sku string when name is missing so the chip always shows
// something legible.
const selectedColorLabel = computed(() => {
    const c = selectedColor.value;
    if (!c) return "";
    return c.name || c.color_sku || editorUi.selectedColorSku || "";
});

// Tier 1 activePreview: the placement the Stage is rendering (the
// selection, else the FIRST placement) -- keying the routing badge off
// the same value the Stage uses means the strip and the composite can
// never describe different placements. Null only for a product with
// no placements.
const selectedPlacement = computed(() => editorUi.effectivePlacement);

// PR-N2: resolve the routed variant via the kind-aware resolver. The
// resolver mirrors the backend per-decoration logic
// (api/src/services/variant_resolver.py) -- specific_colors > catch_all
// (L/D) > single_catch_all, with manual_ld override folded in. The
// legacy A/B "variantRoutingMap" override channel is gone; the
// override surface is now manual_ld (Light/Dark, set via VariantMenu
// + ColorsPanel).
const routedVariant = computed(() => {
    const p = selectedPlacement.value;
    if (!p || !editorUi.selectedColorSku) return null;
    return resolve(p.id ?? p._tempId, editorUi.selectedColorSku);
});

const routedVariantKind = computed(() => routedVariant.value?.kind ?? null);

const variantLabel = computed(() => {
    const v = routedVariant.value;
    if (!v) return "";
    return variantKindLabel(v, {
        productColors: [selectedColor.value].filter(Boolean),
    });
});

const routedArtworkName = computed(() => {
    const v = routedVariant.value;
    if (!v) return null;
    // Stored artwork names carry a GUID-style identity suffix after the
    // first '#' (e.g. "TL-logo-White#e43d37e8..."). That suffix is
    // machinery and must never reach the user -- strip it for the chip
    // label via the shared display-only formatter. formatArtworkName
    // returns "" for null/empty, so the template's `|| "No artwork"`
    // fallback still fires when there is no artwork. (#2018 pattern; Jake)
    return formatArtworkName(v.artwork_filename) || null;
});

const hasFullChain = computed(
    () => selectedColor.value != null && selectedPlacement.value != null,
);

// Real ink-vs-garment contrast (#2791). This badge used to be hardcoded
// ok:true with the ratio picked from the variant kind, so it could not
// fail -- including for the dark-ink-on-dark-garment case it exists to
// catch. The computation is shared with VariantCard via
// utils/artworkReadability so the two surfaces cannot disagree.
//
// Ink source here is the routed variant's own ink_color_hex. Unlike
// VariantCard, this strip has no resolved Artwork record in scope, so
// there is no artwork-level value to prefer. The column is nullable and
// we pass it through UNSUBSTITUTED -- readabilityFor returns null when
// the ink is unknown and the badge then renders nothing rather than an
// unearned green.
const readability = computed(() =>
    readabilityFor({
        inkHex: routedVariant.value?.ink_color_hex ?? null,
        garmentColors: [selectedColor.value].filter(Boolean),
    }),
);
</script>

<style scoped>
.routing-strip-root {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    border-top: 1px solid var(--tf-border, #e0e0e0);
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
    background: var(--tf-bg-subtle, #f8fafc);
    font-size: 12.5px;
    color: var(--tf-text-primary, #133752);
    min-height: 32px;
}

.routing-strip-placeholder {
    color: var(--tf-text-muted, #6b7280);
    font-style: italic;
    font-size: 12px;
}

.routing-strip-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #ffffff;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 50px;
    padding: 2px 10px;
    line-height: 1.4;
    white-space: nowrap;
}

.routing-strip-color-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.12);
    flex-shrink: 0;
}

.routing-strip-arrow {
    color: var(--tf-text-muted, #9aa7b6);
    font-size: 13px;
    flex-shrink: 0;
}

.routing-strip-mi {
    font-size: 14px;
    color: var(--tf-text-muted, #9aa7b6);
    flex-shrink: 0;
}

.routing-strip-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 50px;
    padding: 2px 10px;
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
    margin-left: auto;
}

.routing-strip-badge[data-state="ok"] {
    background: var(--tf-bg-success-subtle, #ecfdf5);
    color: var(--tf-success, #059669);
}
.routing-strip-badge[data-state="ok"] .routing-strip-mi {
    color: var(--tf-success, #059669);
}

.routing-strip-badge[data-state="warn"] {
    background: var(--tf-bg-warning-subtle, #fef3c7);
    color: var(--tf-warning, #d97706);
}
.routing-strip-badge[data-state="warn"] .routing-strip-mi {
    color: var(--tf-warning, #d97706);
}
</style>
